import { c as _c } from "react/compiler-runtime";
import React, { useEffect, useState, useSyncExternalStore } from 'react';
import classNames from 'classnames';
import { DateTime } from 'luxon';
import { useTranslation } from 'react-i18next';
import { faExclamationTriangle } from '@fortawesome/pro-solid-svg-icons/faExclamationTriangle';
import { QuoteChange, QuoteChangePercent, getTimeLabels, isExtendedHours, regularSessionQuote } from '@benzinga/quotes-ui';
import { AFTER_MARKET, NO_TRADING, REGULAR, PRE_MARKET } from '@benzinga/quotes-manager/constants';
import type { QuoteSessionType, DelayedQuote } from '@benzinga/quotes-manager';
import type { Quote } from '@benzinga/quotes-v3-manager';
import { formatPriceWithCurrency } from '@benzinga/utils/numberUtils';
import { useIsMounted } from '@benzinga/hooks';
import { Icon } from '@benzinga/core-ui';
import { withoutStaleDelayedTrades } from '../../utils/quote/delayed-quote';
import { getCurrentSessionType } from '../../utils/quote/session';
import type { BaseQuoteProfile } from '../../utils/quote/types';
interface Props {
  isCrypto?: boolean;
  isRealTime?: boolean;
  profile: BaseQuoteProfile;
  quotes: Quote;
}
const quoteHeaderPriceBoxDefaultClassName = 'quote-header-price-box flex items-center justify-between gap-x-4 p-[14.5px] rounded-md w-full min-h-[80px]';
const getWidthClassByPrice = (price: number | null | undefined, isMain = false): string => {
  const baseWidths = {
    390: isMain ? 'md:w-[390px]' : 'md:w-[415px]',
    420: isMain ? 'md:w-[420px]' : 'md:w-[430px]',
    430: isMain ? 'md:w-[410px]' : 'md:w-[430px]',
    450: isMain ? 'md:w-[430px]' : 'md:w-[450px]',
    480: isMain ? 'md:w-[460px]' : 'md:w-[480px]',
    490: isMain ? 'md:w-[470px]' : 'md:w-[490px]',
    540: isMain ? 'md:w-[520px]' : 'md:w-[540px]'
  };
  const safePrice = price ?? 0;
  const formattedLength = safePrice > 0 && safePrice < 1 ? formatPriceWithCurrency(safePrice, 'USD').length : 0;
  switch (true) {
    case safePrice > 0 && safePrice < 1 && formattedLength <= 7:
      return baseWidths[430];
    case safePrice > 0 && safePrice < 1 && formattedLength <= 9:
      return baseWidths[490];
    case safePrice > 0 && safePrice < 1 && formattedLength > 9:
      return baseWidths[540];
    case safePrice < 100:
      return baseWidths[390];
    case safePrice < 1000:
      return baseWidths[420];
    case safePrice < 10000:
      return baseWidths[450];
    case safePrice < 100000:
      return baseWidths[480];
    default:
      return baseWidths[540];
  }
};
const generatedColoredBoxClassName = (change: number | null, price: number | null | undefined) => {
  const mainWidth = getWidthClassByPrice(price, true);
  return classNames(quoteHeaderPriceBoxDefaultClassName, mainWidth, {
    'bg-bzgray-300 border border-bzgray-500': !change,
    'bg-bzgreen-400/20 border border-green-700/20': change && change > 0,
    'bg-bzred-500/10 border border-red-500/20': change && change < 0
  });
};
const ET = 'America/New_York';
const SECONDARY_PRICE_BOX_HIDE_START_MINUTES = 16 * 60;
const SECONDARY_PRICE_BOX_HIDE_END_MINUTES = 16 * 60 + 15;
const SESSION_CLOCK_MS = 15_000;
const EMPTY_SECONDARY_PRICE_BOX = {
  change: null,
  changePercent: null,
  price: null,
  timeLabel: null
} as const;
export const resolveQuoteSessionType = (profile: BaseQuoteProfile | null | undefined, now?: DateTime | null): QuoteSessionType | null => {
  const baked = profile?.schedule?.type ?? null;
  const schedule = profile?.schedule?.schedule;
  if (now && schedule?.sessions?.length) {
    return getCurrentSessionType(schedule, now);
  }
  return baked;
};
const hasExtendedSessionLabel = (timeLabel: string | null | undefined): boolean => {
  if (!timeLabel) return false;
  const trimmed = timeLabel.trim();
  return trimmed.length > 0 && trimmed !== '-';
};
export const shouldHideSecondaryPriceBox = (profile: BaseQuoteProfile, now?: DateTime | null): boolean => {
  const session = resolveQuoteSessionType(profile, now);
  if (session !== AFTER_MARKET && session !== NO_TRADING) {
    // Pre-market is the only non-after-hours session that shows the secondary box.
    // REGULAR, null, and any other unknown session stay hidden.
    return session !== PRE_MARKET;
  }
  const clock = (now ?? DateTime.now()).setZone(ET);
  const nowEtMinutes = clock.hour * 60 + clock.minute;
  return nowEtMinutes >= SECONDARY_PRICE_BOX_HIDE_START_MINUTES && nowEtMinutes < SECONDARY_PRICE_BOX_HIDE_END_MINUTES;
};
let clientSessionNowMs = 0;
const sessionClockListeners = new Set<() => void>();
let sessionClockInterval: ReturnType<typeof setInterval> | null = null;
export const readClientSessionNow = (): number | null => {
  if (typeof window === 'undefined') return null;
  if (clientSessionNowMs === 0) clientSessionNowMs = Date.now();
  return clientSessionNowMs;
};
const getServerSessionNow = (): number | null => null;
export const subscribeToSessionClock = (onStoreChange: () => void): (() => void) => {
  if (typeof window !== 'undefined') {
    clientSessionNowMs = Date.now();
  }
  sessionClockListeners.add(onStoreChange);
  if (sessionClockInterval == null) {
    sessionClockInterval = setInterval(() => {
      clientSessionNowMs = Date.now();
      sessionClockListeners.forEach(listener => listener());
    }, SESSION_CLOCK_MS);
  }
  return () => {
    sessionClockListeners.delete(onStoreChange);
    if (sessionClockListeners.size === 0 && sessionClockInterval != null) {
      clearInterval(sessionClockInterval);
      sessionClockInterval = null;
    }
  };
};

/** Current time on the client; null during SSR and hydration so the baked session is what paints. */
const useQuoteSessionNow = (): DateTime | null => {
  const nowMs = useSyncExternalStore(subscribeToSessionClock, readClientSessionNow, getServerSessionNow);
  if (nowMs == null) return null;
  return DateTime.fromMillis(nowMs);
};
export const QuoteHeaderPriceBox: React.FC<Props> = t0 => {
  "use memo";

  const $ = _c(30);
  const {
    isCrypto,
    isRealTime,
    profile: rawProfile,
    quotes
  } = t0;
  const {
    t
  } = useTranslation("quote");
  const sessionNow = useQuoteSessionNow();
  let t1;
  if ($[0] !== rawProfile.richQuoteData || $[1] !== sessionNow) {
    t1 = withoutStaleDelayedTrades(rawProfile.richQuoteData, sessionNow?.toMillis() ?? null);
    $[0] = rawProfile.richQuoteData;
    $[1] = sessionNow;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const richQuoteData = t1;
  let t2;
  if ($[3] !== rawProfile || $[4] !== richQuoteData) {
    t2 = richQuoteData === rawProfile.richQuoteData ? rawProfile : {
      ...rawProfile,
      richQuoteData
    };
    $[3] = rawProfile;
    $[4] = richQuoteData;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const profile = t2;
  const [isMountedDelayed, setIsMountedDelayed] = useState(false);
  const mounted = useIsMounted();
  let t3;
  let t4;
  if ($[6] !== mounted) {
    t3 = () => {
      if (!mounted) {
        return;
      }
      const timer = setTimeout(() => {
        setIsMountedDelayed(true);
      }, 2000);
      return () => clearTimeout(timer);
    };
    t4 = [mounted];
    $[6] = mounted;
    $[7] = t3;
    $[8] = t4;
  } else {
    t3 = $[7];
    t4 = $[8];
  }
  useEffect(t3, t4);
  const priceDataNotAvailable = !profile?.richQuoteData?.lastTradePrice && !profile?.richQuoteData?.close;
  const showDelayedMessageSpacer = !profile.isPrivate;
  const t5 = !priceDataNotAvailable && showDelayedMessageSpacer;
  let t6;
  if ($[9] !== priceDataNotAvailable || $[10] !== t5) {
    t6 = classNames("w-full flex flex-col items-center justify-center", {
      "h-8": priceDataNotAvailable,
      "min-h-[110px]": t5
    });
    $[9] = priceDataNotAvailable;
    $[10] = t5;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  let t7;
  if ($[12] !== isCrypto || $[13] !== isRealTime || $[14] !== priceDataNotAvailable || $[15] !== profile || $[16] !== quotes || $[17] !== sessionNow) {
    t7 = <div className="flex flex-wrap lg:flex-nowrap gap-2 w-full">{priceDataNotAvailable ? <PriceDataUnavailable isPrivate={profile.isPrivate} /> : isCrypto ? <CryptoPriceBox isRealTime={isRealTime} profile={profile} quotes={quotes} /> : profile.isPrivate ? <PrivatePriceBox profile={profile} /> : <><MainPriceBox isRealTime={isRealTime} profile={profile} quotes={quotes} sessionNow={sessionNow} />{!isCrypto && !shouldHideSecondaryPriceBox(profile, sessionNow) && <SecondaryPriceBox profile={profile} quotes={quotes} sessionNow={sessionNow} />}</>}</div>;
    $[12] = isCrypto;
    $[13] = isRealTime;
    $[14] = priceDataNotAvailable;
    $[15] = profile;
    $[16] = quotes;
    $[17] = sessionNow;
    $[18] = t7;
  } else {
    t7 = $[18];
  }
  let t8;
  if ($[19] !== isMountedDelayed || $[20] !== isRealTime || $[21] !== priceDataNotAvailable || $[22] !== profile.wiim || $[23] !== showDelayedMessageSpacer || $[24] !== t) {
    t8 = showDelayedMessageSpacer && <div className={classNames("w-full whitespace-nowrap text-xs mt-1 text-gray-600 h-[16px]", {
      "mb-2": !profile?.wiim
    })}>{!priceDataNotAvailable && !isRealTime && isMountedDelayed && <i>{t("Quote.Market.minutes-delayed", {
          minutes: 15
        })}</i>}</div>;
    $[19] = isMountedDelayed;
    $[20] = isRealTime;
    $[21] = priceDataNotAvailable;
    $[22] = profile.wiim;
    $[23] = showDelayedMessageSpacer;
    $[24] = t;
    $[25] = t8;
  } else {
    t8 = $[25];
  }
  let t9;
  if ($[26] !== t6 || $[27] !== t7 || $[28] !== t8) {
    t9 = <div className={t6}>{t7}{t8}</div>;
    $[26] = t6;
    $[27] = t7;
    $[28] = t8;
    $[29] = t9;
  } else {
    t9 = $[29];
  }
  return t9;
};
const MainPriceBox: React.FC<Props & {
  sessionNow: DateTime | null;
}> = t0 => {
  "use memo";

  const $ = _c(28);
  const {
    isRealTime,
    profile,
    quotes,
    sessionNow
  } = t0;
  const {
    t
  } = useTranslation("quote");
  let t1;
  let values;
  if ($[0] !== isRealTime || $[1] !== profile || $[2] !== quotes || $[3] !== sessionNow || $[4] !== t) {
    values = getMainPriceBoxValues(profile, quotes, isRealTime, t, sessionNow);
    t1 = generatedColoredBoxClassName(values.change, values.price);
    $[0] = isRealTime;
    $[1] = profile;
    $[2] = quotes;
    $[3] = sessionNow;
    $[4] = t;
    $[5] = t1;
    $[6] = values;
  } else {
    t1 = $[5];
    values = $[6];
  }
  const className = t1;
  let t2;
  if ($[7] !== values.price) {
    t2 = typeof values.price === "number" && <PriceText price={values.price} />;
    $[7] = values.price;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  let t4;
  if ($[9] !== values.change) {
    t3 = <QuoteChangeIcon change={values.change} />;
    t4 = typeof values.change === "number" && <QuoteChange absoluteChange={values.change} positiveColor="text-green-600" />;
    $[9] = values.change;
    $[10] = t3;
    $[11] = t4;
  } else {
    t3 = $[10];
    t4 = $[11];
  }
  let t5;
  if ($[12] !== values.change || $[13] !== values.changePercent) {
    t5 = typeof values.changePercent === "number" && typeof values.change === "number" && <QuoteHeaderChangePercent change={values.change} changePercent={values.changePercent} />;
    $[12] = values.change;
    $[13] = values.changePercent;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  let t6;
  if ($[15] !== t3 || $[16] !== t4 || $[17] !== t5) {
    t6 = <div className="quote-change-data flex items-center gap-2 text-bzgray-900 text-base min-h-[26px]">{t3}{t4}{t5}</div>;
    $[15] = t3;
    $[16] = t4;
    $[17] = t5;
    $[18] = t6;
  } else {
    t6 = $[18];
  }
  let t7;
  if ($[19] !== values.timeLabel) {
    t7 = <TimeLabel text={values.timeLabel} />;
    $[19] = values.timeLabel;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  let t8;
  if ($[21] !== t6 || $[22] !== t7) {
    t8 = <div className="ml-auto min-w-[200px] lg:min-w-[unset]">{t6}{t7}</div>;
    $[21] = t6;
    $[22] = t7;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  let t9;
  if ($[24] !== className || $[25] !== t2 || $[26] !== t8) {
    t9 = <div className={className}>{t2}{t8}</div>;
    $[24] = className;
    $[25] = t2;
    $[26] = t8;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  return t9;
};
const PrivatePriceBox: React.FC<{
  profile: BaseQuoteProfile;
}> = ({
  profile
}) => {
  const price = profile.richQuoteData?.lastTradePrice ?? profile.richQuoteData?.close ?? null;
  const previousClosePrice = profile.richQuoteData?.previousClosePrice ?? null;
  const change = typeof price === 'number' && typeof previousClosePrice === 'number' ? price - previousClosePrice : null;
  const changePercent = typeof change === 'number' && previousClosePrice ? change / previousClosePrice * 100 : null;
  const className = generatedColoredBoxClassName(change, price);
  return <div className={className}>
      {typeof price === 'number' && <PriceText price={price} />}
      <div className="ml-auto min-w-[200px] lg:min-w-[unset]">
        <div className="quote-change-data flex items-center gap-2 text-bzgray-900 text-base min-h-[26px]">
          <QuoteChangeIcon change={change} />
          {typeof change === 'number' && <QuoteChange absoluteChange={change} positiveColor="text-green-600" />}
          {typeof changePercent === 'number' && typeof change === 'number' && <QuoteHeaderChangePercent change={change} changePercent={changePercent} />}
        </div>
        <TimeLabel text="Nasdaq Private Market" />
      </div>
    </div>;
};
const SecondaryPriceBox: React.FC<Props & {
  sessionNow: DateTime | null;
}> = t0 => {
  "use memo";

  const $ = _c(28);
  const {
    profile,
    quotes,
    sessionNow
  } = t0;
  const {
    t
  } = useTranslation("quote");
  let t1;
  let t2;
  let values;
  if ($[0] !== profile || $[1] !== quotes || $[2] !== sessionNow || $[3] !== t) {
    t2 = Symbol.for("react.early_return_sentinel");
    bb0: {
      values = getSecondaryPriceBoxValues(profile, quotes, t, sessionNow);
      if (!values.price) {
        t2 = null;
        break bb0;
      }
      t1 = getWidthClassByPrice(values.price, false);
    }
    $[0] = profile;
    $[1] = quotes;
    $[2] = sessionNow;
    $[3] = t;
    $[4] = t1;
    $[5] = t2;
    $[6] = values;
  } else {
    t1 = $[4];
    t2 = $[5];
    values = $[6];
  }
  if (t2 !== Symbol.for("react.early_return_sentinel")) {
    return t2;
  }
  const widthClass = t1;
  const t3 = `${quoteHeaderPriceBoxDefaultClassName} ${widthClass} bg-bzgray-200 text-bzgray-900 border border-bzgray-400`;
  let t4;
  if ($[7] !== values.price) {
    t4 = typeof values.price === "number" && <PriceText price={values.price} />;
    $[7] = values.price;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  let t5;
  let t6;
  if ($[9] !== values.change) {
    t5 = <QuoteChangeIcon change={values.change} />;
    t6 = typeof values.change === "number" && <QuoteChange absoluteChange={values.change} positiveColor="text-green-600" />;
    $[9] = values.change;
    $[10] = t5;
    $[11] = t6;
  } else {
    t5 = $[10];
    t6 = $[11];
  }
  let t7;
  if ($[12] !== values.change || $[13] !== values.changePercent) {
    t7 = typeof values.changePercent === "number" && typeof values.change === "number" && <QuoteHeaderChangePercent change={values.change} changePercent={values.changePercent} />;
    $[12] = values.change;
    $[13] = values.changePercent;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  let t8;
  if ($[15] !== t5 || $[16] !== t6 || $[17] !== t7) {
    t8 = <div className="quote-change-data flex items-center gap-2 text-base min-h-[26px]">{t5}{t6}{t7}</div>;
    $[15] = t5;
    $[16] = t6;
    $[17] = t7;
    $[18] = t8;
  } else {
    t8 = $[18];
  }
  let t9;
  if ($[19] !== values.timeLabel) {
    t9 = <TimeLabel text={values.timeLabel} />;
    $[19] = values.timeLabel;
    $[20] = t9;
  } else {
    t9 = $[20];
  }
  let t10;
  if ($[21] !== t8 || $[22] !== t9) {
    t10 = <div className="ml-auto min-w-[200px] lg:min-w-[unset]">{t8}{t9}</div>;
    $[21] = t8;
    $[22] = t9;
    $[23] = t10;
  } else {
    t10 = $[23];
  }
  let t11;
  if ($[24] !== t10 || $[25] !== t3 || $[26] !== t4) {
    t11 = <div className={t3}>{t4}{t10}</div>;
    $[24] = t10;
    $[25] = t3;
    $[26] = t4;
    $[27] = t11;
  } else {
    t11 = $[27];
  }
  return t11;
};
const CryptoPriceBox: React.FC<Props> = t0 => {
  "use memo";

  const $ = _c(30);
  const {
    isRealTime,
    profile,
    quotes
  } = t0;
  const {
    t
  } = useTranslation("quote");
  const profileQuote = profile?.richQuoteData;
  const price = isRealTime ? (quotes as Quote)?.price ?? profileQuote?.lastTradePrice : profileQuote?.lastTradePrice;
  const change = isRealTime ? quotes?.change ?? profileQuote?.change ?? null : profileQuote?.change ?? null;
  const changePercent = isRealTime ? quotes?.changePercent ?? profileQuote?.changePercent : profileQuote?.changePercent;
  const lastTradeTime = isRealTime ? quotes?.time || quotes?.lastTradeTime || profileQuote?.lastTradeTime : profileQuote?.lastTradeTime;
  let t1;
  if ($[0] !== lastTradeTime) {
    t1 = lastTradeTime ? DateTime.fromMillis(lastTradeTime).setZone("UTC").toFormat("h:mm a ZZZZ") : null;
    $[0] = lastTradeTime;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const updateTime = t1;
  let t2;
  if ($[2] !== lastTradeTime || $[3] !== t || $[4] !== updateTime) {
    t2 = lastTradeTime ? `${t("Quote.Price.last-update")}: ${updateTime}` : "-";
    $[2] = lastTradeTime;
    $[3] = t;
    $[4] = updateTime;
    $[5] = t2;
  } else {
    t2 = $[5];
  }
  const timeLabel = t2;
  const t3 = change as number | null;
  let t4;
  if ($[6] !== price || $[7] !== t3) {
    t4 = generatedColoredBoxClassName(t3, price);
    $[6] = price;
    $[7] = t3;
    $[8] = t4;
  } else {
    t4 = $[8];
  }
  const className = t4;
  const t5 = `${className} whitespace-nowrap`;
  let t6;
  if ($[9] !== price) {
    t6 = typeof price === "number" && <PriceText price={price} />;
    $[9] = price;
    $[10] = t6;
  } else {
    t6 = $[10];
  }
  let t7;
  let t8;
  if ($[11] !== change) {
    t7 = <QuoteChangeIcon change={change} />;
    t8 = typeof change === "number" && <QuoteChange absoluteChange={change} positiveColor="text-green-600" />;
    $[11] = change;
    $[12] = t7;
    $[13] = t8;
  } else {
    t7 = $[12];
    t8 = $[13];
  }
  let t9;
  if ($[14] !== change || $[15] !== changePercent) {
    t9 = typeof changePercent === "number" && <QuoteHeaderChangePercent change={change} changePercent={changePercent} />;
    $[14] = change;
    $[15] = changePercent;
    $[16] = t9;
  } else {
    t9 = $[16];
  }
  let t10;
  if ($[17] !== t7 || $[18] !== t8 || $[19] !== t9) {
    t10 = <div className="quote-change-data flex items-center gap-2 text-bzred-500 text-base min-h-[26px]">{t7}{t8}{t9}</div>;
    $[17] = t7;
    $[18] = t8;
    $[19] = t9;
    $[20] = t10;
  } else {
    t10 = $[20];
  }
  let t11;
  if ($[21] !== timeLabel) {
    t11 = <TimeLabel text={timeLabel} />;
    $[21] = timeLabel;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== t10 || $[24] !== t11) {
    t12 = <div>{t10}{t11}</div>;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
  } else {
    t12 = $[25];
  }
  let t13;
  if ($[26] !== t12 || $[27] !== t5 || $[28] !== t6) {
    t13 = <div className={t5}>{t6}{t12}</div>;
    $[26] = t12;
    $[27] = t5;
    $[28] = t6;
    $[29] = t13;
  } else {
    t13 = $[29];
  }
  return t13;
};
const TimeLabel = ({
  text
}: {
  text: string | null;
}) => {
  // Real-time timestamp: the SSR snapshot and live client value legitimately differ.
  // Suppress the hydration warning for this text node only — no rendering logic changes.
  return <div className="text-bzgray-600 text-xs font-semibold uppercase mt-1 whitespace-nowrap" suppressHydrationWarning>
      {text}
    </div>;
};
const PriceDataUnavailable: React.FC<{
  isPrivate?: boolean;
}> = ({
  isPrivate
}) => {
  return <div className="flex items-center gap-1 text-bzred-500 text-sm font-semibold h-8">
      <Icon className="h-5 w-5 fill-current mt-2" icon={faExclamationTriangle} />
      <span>{isPrivate ? 'Private market price is not available' : 'Price data is not available'}</span>
    </div>;
};
const PriceText = ({
  price
}: {
  price: number;
}) => {
  // Real-time price: the SSR snapshot and the live client value legitimately differ.
  // Suppress the hydration warning for this text node only (client value wins) — the
  // rendering logic and conditional rendering are unchanged from the original.
  return <div className="text-3xl md:text-5xl text-bzgray-900 leading-none" suppressHydrationWarning>
      {formatPriceWithCurrency(price, 'USD')}
    </div>;
};
const QuoteHeaderChangePercent = ({
  change,
  changePercent
}: {
  change: number | null;
  changePercent: number;
}) => {
  return <span className={classNames('px-1 py-[1px] rounded', {
    'bg-bzgray-100': !change,
    'bg-bzgreen-500/20': change && change > 0,
    'bg-bzred-500/10': change && change < 0
  })}>
      <QuoteChangePercent percentageChange={changePercent} positiveColor="text-green-600" />
    </span>;
};
const QuoteChangeIcon = ({
  change
}: {
  change: number | null;
}) => {
  if (!change) return null;
  return <div className={classNames('flex items-center justify-center p-3 rounded w-5 h-5', {
    'bg-bzgray-100': !change,
    'bg-bzgreen-500/20': change && change > 0,
    'bg-bzred-500/10': change && change < 0,
    'text-bzred-500': change && change < 0,
    'text-green-600': change && change > 0
  })}>
      <span>
        <svg className="feather feather-arrow-up-right" fill="none" height="20" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="3" viewBox="0 0 24 24" width="20" xmlns="http://www.w3.org/2000/svg">
          {change > 0 ? <>
              <path d="M7 17L17 7"></path>
              <path d="M7 7L17 7 17 17"></path>
            </> : <>
              <path d="M7 7L17 17"></path>
              <path d="M17 7L17 17 7 17"></path>
            </>}
        </svg>
      </span>
    </div>;
};
export const getMainPriceBoxValues = (profile: BaseQuoteProfile, quotes: Quote, isRealTime?: boolean, translateFn?: (key: string, options?: {
  ns?: string;
}) => string, now?: DateTime | null): {
  change: number | null;
  changePercent: number | null;
  price: number | null | undefined;
  timeLabel: string;
} => {
  const close = profile?.richQuoteData?.close;
  const previousClosePrice = profile?.richQuoteData?.previousClosePrice;
  const session = resolveQuoteSessionType(profile, now);
  const isRegular = session === REGULAR;
  const isNoTrading = session === NO_TRADING;
  const isPremarket = session === PRE_MARKET;
  let price: number | null | undefined = null;
  let change: number | null = null;
  let changePercent: number | null = null;
  let timeLabel = '-';
  if (isRegular) {
    price = isRealTime ? (quotes as Quote)?.price ?? quotes?.askPrice : profile?.richQuoteData?.lastTradePrice ?? profile?.richQuoteData?.close;
    change = price && previousClosePrice ? price - previousClosePrice : quotes?.change ?? null;
    changePercent = change && previousClosePrice ? change / previousClosePrice * 100 : quotes?.changePercent ?? null;
    const quoteData = isRealTime ? quotes as DelayedQuote : profile?.richQuoteData;
    timeLabel = quoteData ? getTimeLabels(quoteData, session ?? undefined, {
      isRealTime,
      longDate: true
    }, translateFn).timestampClose : '-';
  } else if (close && previousClosePrice) {
    price = close;
    change = price - previousClosePrice;
    changePercent = change / previousClosePrice * 100;
  } else if (isNoTrading && previousClosePrice) {
    price = previousClosePrice;
    change = quotes?.change ?? null;
    changePercent = quotes?.changePercent ?? null;
  } else if (isPremarket && previousClosePrice) {
    price = previousClosePrice;
    change = quotes?.change ?? null;
    changePercent = quotes?.changePercent ?? null;
  } else if (!close) {
    const [newPrice, newChange, newChangePercent] = regularSessionQuote(profile?.richQuoteData?.close || quotes?.close, profile?.richQuoteData?.previousClose || quotes?.previousClose, profile?.richQuoteData?.lastTradePrice);
    price = newPrice;
    change = newChange ?? null;
    changePercent = newChangePercent ?? null;
  }
  if (price == null) {
    price = close ?? profile?.richQuoteData?.lastTradePrice ?? null;
    if (change == null && typeof price === 'number' && typeof previousClosePrice === 'number') {
      change = price - previousClosePrice;
      changePercent = previousClosePrice !== 0 ? change / previousClosePrice * 100 : changePercent;
    }
  }
  if (!(isRegular && isRealTime)) {
    timeLabel = profile?.richQuoteData ? getTimeLabels(profile.richQuoteData, session ?? undefined, {
      longDate: true
    }, translateFn).timestampClose : '-';
  }
  return {
    change,
    changePercent,
    price,
    timeLabel
  };
};
export const getSecondaryPriceBoxValues = (profile: BaseQuoteProfile, quotes?: Quote, translateFn?: (key: string, options?: {
  ns?: string;
}) => string, now?: DateTime | null): {
  change: number | null;
  changePercent: number | null;
  price: number | null | undefined;
  timeLabel: string | null;
} => {
  const session = resolveQuoteSessionType(profile, now);
  // REGULAR and unknown (null) never show a second price. The old final branch
  // treated any other value — including null — as extended hours and rendered
  // ethPrice against the previous close with an empty label.
  if (session == null || !isExtendedHours(session)) {
    return {
      ...EMPTY_SECONDARY_PRICE_BOX
    };
  }
  const isPremarket = session === PRE_MARKET;
  const closePrice = profile?.richQuoteData?.close;
  const ethPrice = profile?.richQuoteData?.ethPrice;
  const previousClosePrice = profile?.richQuoteData?.previousClosePrice;
  let price: number | null | undefined = null;
  let change: number | null = null;
  let changePercent: number | null = null;
  if (isPremarket && quotes) {
    price = (quotes as Quote)?.price ?? quotes?.askPrice ?? profile?.richQuoteData?.lastTradePrice;
    change = quotes?.change ?? (price && previousClosePrice ? price - previousClosePrice : null);
    changePercent = quotes?.changePercent ?? (change && previousClosePrice ? change / previousClosePrice * 100 : null);
  } else if (!isPremarket && closePrice !== undefined && ethPrice !== undefined) {
    price = ethPrice;
    change = price - closePrice;
    changePercent = change / closePrice * 100;
  } else if (isPremarket && ethPrice !== undefined && previousClosePrice !== undefined && profile?.richQuoteData) {
    price = ethPrice;
    change = price - previousClosePrice;
    changePercent = change / previousClosePrice * 100;
  }
  const timeLabel = profile?.richQuoteData ? getTimeLabels(profile.richQuoteData, session, undefined, translateFn).timestampEth : null;
  if (price == null || !hasExtendedSessionLabel(timeLabel)) {
    return {
      ...EMPTY_SECONDARY_PRICE_BOX
    };
  }
  return {
    change,
    changePercent,
    price,
    timeLabel
  };
};
export default QuoteHeaderPriceBox;